/**
 * Medical Leave Service
 * API integration for Medical Leaves (Licenças Médicas) CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de Licenças Médicas
 */
import apiClient from './apiClient';
import {
  MedicalLeave,
  MedicalLeaveFilters,
  MedicalLeaveFormData,
  MedicalLeaveListResponse,
  CertificateInfo,
  LEAVE_REASONS,
  UF_OPTIONS,
} from '../types/medicalLeave';

// Re-export types for convenience
export * from '../types/medicalLeave';

export interface MedicalLeaveListParams extends MedicalLeaveFilters {
  page?: number;
  limit?: number;
}

class MedicalLeaveService {
  private baseUrl = '/medical-leaves';

  /**
   * Get paginated list of medical leaves
   */
  async getMedicalLeaves(params: MedicalLeaveListParams = {}): Promise<MedicalLeaveListResponse> {
    const { page = 1, limit = 25, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.employeeId) queryParams.append('employeeId', filters.employeeId.toString());
    if (filters.cidCode) queryParams.append('cidCode', filters.cidCode);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // httpClient.get() already unwraps axios response.data
    // so response = { message, data: [...], pagination: {...} }
    return {
      medicalLeaves: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page || page,
      limit: response.pagination?.limit || limit,
      totalPages: response.pagination?.totalPages || Math.ceil((response.pagination?.total ?? 0) / limit),
    };
  }

  /**
   * Get medical leave by ID
   */
  async getMedicalLeaveById(id: string | number): Promise<MedicalLeave> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new medical leave
   */
  async createMedicalLeave(data: MedicalLeaveFormData): Promise<MedicalLeave> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update medical leave by ID
   */
  async updateMedicalLeave(id: string | number, data: Partial<MedicalLeaveFormData>): Promise<MedicalLeave> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete medical leave by ID (hard delete)
   */
  async deleteMedicalLeave(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get leave reasons list
   */
  async getLeaveReasons(): Promise<string[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/leave-reasons`);
      return response.data.data || LEAVE_REASONS;
    } catch {
      // Fallback to local constants if API fails
      return LEAVE_REASONS;
    }
  }

  /**
   * Upload certificate (upsert - create or update)
   */
  async uploadCertificate(medicalLeaveId: string | number, file: File): Promise<CertificateInfo> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `${this.baseUrl}/${medicalLeaveId}/certificate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.data || response.data;
  }

  /**
   * Get certificate info
   */
  async getCertificate(medicalLeaveId: string | number): Promise<CertificateInfo | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${medicalLeaveId}/certificate`);
      return response.data.data || response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Arquivo do atestado, baixado pelo cliente autenticado. A URL da API não pode ir direto para
   * iframe, imagem ou nova aba: essa navegação não leva o login nem passa pelo CORS (#558).
   */
  async fetchCertificateFile(medicalLeaveId: string | number): Promise<Blob> {
    const response = await apiClient.getClient().get<Blob>(
      `${this.baseUrl}/${medicalLeaveId}/certificate/download`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Delete certificate
   */
  async deleteCertificate(medicalLeaveId: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${medicalLeaveId}/certificate`);
  }

  /**
   * Get UF options for select dropdowns
   */
  getUfOptions(): Array<{ value: string; label: string }> {
    return UF_OPTIONS.map(uf => ({ value: uf, label: uf }));
  }

  /**
   * Get leave reason options for select dropdowns
   */
  getLeaveReasonOptions(): Array<{ value: string; label: string }> {
    return LEAVE_REASONS.map(reason => ({ value: reason, label: reason }));
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const medicalLeaveService = new MedicalLeaveService();
export default medicalLeaveService;
